// Police ID Verification Redux Slice - store/slices/police-verification.slice.ts
import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { FlaggedCitizen, IdentityVerification } from '@/types/police.types'

export interface VerificationFilters {
  page: number
  pageSize: number
  search: string
  status: 'all' | 'pending' | 'verified' | 'rejected' | 'flagged'
  method: 'all' | 'qr_scan' | 'manual_search' | 'camera_detection'
  officerId: string
  dateFrom: Date | null
  dateTo: Date | null
  sortBy: string
  sortOrder: 'asc' | 'desc'
  location: string
  responseTimeFilter: 'all' | 'fast' | 'slow' | 'timeout'
}

export interface FlaggedCitizenFilters {
  page: number
  pageSize: number
  search: string
  priority: 'all' | 'low' | 'medium' | 'high' | 'wanted'
  status: 'all' | 'active' | 'resolved' | 'reviewing'
  flaggedBy: string
  dateFrom: Date | null
  dateTo: Date | null
  sortBy: string
  sortOrder: 'asc' | 'desc'
  location: string
  hasLastSeen?: boolean
  hasWarrant?: boolean
}

export interface VerificationStatistics {
  totalVerifications: number
  pendingVerifications: number
  successfulVerifications: number
  flaggedCitizens: number
  averageResponseTime: number
  verificationRate: number
  todayVerifications: number
  weeklyGrowth: number
  flaggedGrowth: number
  activeOfficers: number
}

export interface VerificationPagination {
  total: number
  page: number
  pageSize: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

interface PoliceVerificationState {
  // Verification Data
  verifications: IdentityVerification[]
  flaggedCitizens: FlaggedCitizen[]
  statistics: VerificationStatistics | null

  // Filters & Pagination
  verificationFilters: VerificationFilters
  flaggedFilters: FlaggedCitizenFilters
  verificationPagination: VerificationPagination | null
  flaggedPagination: VerificationPagination | null

  // UI State
  loading: boolean
  error: string | null
  selectedVerifications: string[]
  selectedFlaggedCitizens: string[]

  // Modal/Dialog State
  showVerificationDialog: boolean
  showFlagDialog: boolean
  showDetailsDrawer: boolean
  showFiltersDrawer: boolean
  showExportDialog: boolean

  // Selected Items
  selectedVerification: IdentityVerification | null
  selectedCitizen: FlaggedCitizen | null

  // Quick Actions
  quickVerificationMode: boolean
  lastVerificationResult: IdentityVerification | null
  recentSearches: string[]

  // Real-time Features
  realTimeEnabled: boolean
  pendingNotifications: number

  // Cache management
  lastRefresh: string | null
  cacheTimestamp: number
}

const initialVerificationFilters: VerificationFilters = {
  page: 1,
  pageSize: 25,
  search: '',
  status: 'all',
  method: 'all',
  officerId: 'all',
  dateFrom: null,
  dateTo: null,
  sortBy: 'requestedAt',
  sortOrder: 'desc',
  location: 'all',
  responseTimeFilter: 'all',
}

const initialFlaggedFilters: FlaggedCitizenFilters = {
  page: 1,
  pageSize: 25,
  search: '',
  priority: 'all',
  status: 'all',
  flaggedBy: 'all',
  dateFrom: null,
  dateTo: null,
  sortBy: 'flaggedAt',
  sortOrder: 'desc',
  location: 'all',
  hasWarrant: false,
}

const initialState: PoliceVerificationState = {
  // Data
  verifications: [],
  flaggedCitizens: [],
  statistics: null,

  // Filters & Pagination
  verificationFilters: initialVerificationFilters,
  flaggedFilters: initialFlaggedFilters,
  verificationPagination: null,
  flaggedPagination: null,

  // UI State
  loading: false,
  error: null,
  selectedVerifications: [],
  selectedFlaggedCitizens: [],

  // Modal/Dialog State
  showVerificationDialog: false,
  showFlagDialog: false,
  showDetailsDrawer: false,
  showFiltersDrawer: false,
  showExportDialog: false,

  // Selected Items
  selectedVerification: null,
  selectedCitizen: null,

  // Quick Actions
  quickVerificationMode: false,
  lastVerificationResult: null,
  recentSearches: [],

  // Real-time Features
  realTimeEnabled: true,
  pendingNotifications: 0,

  // Cache management
  lastRefresh: null,
  cacheTimestamp: 0,
}

const policeVerificationSlice = createSlice({
  name: 'policeVerification',
  initialState,
  reducers: {
    // === Data Management ===
    setVerifications: (state, action: PayloadAction<IdentityVerification[]>) => {
      state.verifications = action.payload
      state.loading = false
      state.error = null
      state.lastRefresh = new Date().toISOString()
    },

    setFlaggedCitizens: (state, action: PayloadAction<FlaggedCitizen[]>) => {
      state.flaggedCitizens = action.payload
      state.loading = false
      state.error = null
    },

    setStatistics: (state, action: PayloadAction<VerificationStatistics>) => {
      state.statistics = action.payload
    },

    setVerificationPagination: (state, action: PayloadAction<VerificationPagination>) => {
      state.verificationPagination = action.payload
    },

    setFlaggedPagination: (state, action: PayloadAction<VerificationPagination>) => {
      state.flaggedPagination = action.payload
    },

    // === Loading & Error States ===
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // === Verification Filters ===
    setVerificationFilters: (state, action: PayloadAction<Partial<VerificationFilters>>) => {
      state.verificationFilters = { ...state.verificationFilters, ...action.payload }
      if (!('page' in action.payload)) {
        state.verificationFilters.page = 1
      }
    },

    resetVerificationFilters: (state) => {
      state.verificationFilters = initialVerificationFilters
      state.selectedVerifications = []
    },

    // === Flagged Citizens Filters ===
    setFlaggedFilters: (state, action: PayloadAction<Partial<FlaggedCitizenFilters>>) => {
      state.flaggedFilters = { ...state.flaggedFilters, ...action.payload }
      if (!('page' in action.payload)) {
        state.flaggedFilters.page = 1
      }
    },

    resetFlaggedFilters: (state) => {
      state.flaggedFilters = initialFlaggedFilters
      state.selectedFlaggedCitizens = []
    },

    // === Selection Management ===
    setSelectedVerifications: (state, action: PayloadAction<string[]>) => {
      state.selectedVerifications = action.payload
    },

    toggleVerificationSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload
      const index = state.selectedVerifications.indexOf(id)
      if (index >= 0) {
        state.selectedVerifications.splice(index, 1)
      } else {
        state.selectedVerifications.push(id)
      }
    },

    selectAllVerifications: (state) => {
      state.selectedVerifications = state.verifications.map((v) => v.id)
    },

    clearVerificationSelection: (state) => {
      state.selectedVerifications = []
    },

    setSelectedFlaggedCitizens: (state, action: PayloadAction<string[]>) => {
      state.selectedFlaggedCitizens = action.payload
    },

    toggleFlaggedSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload
      const index = state.selectedFlaggedCitizens.indexOf(id)
      if (index >= 0) {
        state.selectedFlaggedCitizens.splice(index, 1)
      } else {
        state.selectedFlaggedCitizens.push(id)
      }
    },

    clearFlaggedSelection: (state) => {
      state.selectedFlaggedCitizens = []
    },

    // === Modal/Dialog Management ===
    openVerificationDialog: (state) => {
      state.showVerificationDialog = true
    },

    closeVerificationDialog: (state) => {
      state.showVerificationDialog = false
      state.quickVerificationMode = false
    },

    openFlagDialog: (state, action: PayloadAction<FlaggedCitizen | null>) => {
      state.showFlagDialog = true
      state.selectedCitizen = action.payload
    },

    closeFlagDialog: (state) => {
      state.showFlagDialog = false
      state.selectedCitizen = null
    },

    openDetailsDrawer: (
      state,
      action: PayloadAction<{
        type: 'verification' | 'citizen'
        item: IdentityVerification | FlaggedCitizen
      }>
    ) => {
      state.showDetailsDrawer = true
      if (action.payload.type === 'verification') {
        state.selectedVerification = action.payload.item as IdentityVerification
      } else {
        state.selectedCitizen = action.payload.item as FlaggedCitizen
      }
    },

    closeDetailsDrawer: (state) => {
      state.showDetailsDrawer = false
      state.selectedVerification = null
      state.selectedCitizen = null
    },

    openFiltersDrawer: (state) => {
      state.showFiltersDrawer = true
    },

    closeFiltersDrawer: (state) => {
      state.showFiltersDrawer = false
    },

    openExportDialog: (state) => {
      state.showExportDialog = true
    },

    closeExportDialog: (state) => {
      state.showExportDialog = false
    },

    // === Data Operations ===
    addVerification: (state, action: PayloadAction<IdentityVerification>) => {
      state.verifications.unshift(action.payload)
      state.lastVerificationResult = action.payload

      // Update statistics
      if (state.statistics) {
        state.statistics.totalVerifications += 1
        if (action.payload.status === 'verified') {
          state.statistics.successfulVerifications += 1
        }
        if (action.payload.status === 'pending') {
          state.statistics.pendingVerifications += 1
        }
      }
    },

    updateVerification: (state, action: PayloadAction<IdentityVerification>) => {
      const index = state.verifications.findIndex((v) => v.id === action.payload.id)
      if (index >= 0) {
        state.verifications[index] = action.payload
      }

      // Update selected if it's the same
      if (state.selectedVerification?.id === action.payload.id) {
        state.selectedVerification = action.payload
      }
    },

    removeVerification: (state, action: PayloadAction<string>) => {
      const id = action.payload
      state.verifications = state.verifications.filter((v) => v.id !== id)
      state.selectedVerifications = state.selectedVerifications.filter((vid) => vid !== id)

      // Update statistics
      if (state.statistics) {
        state.statistics.totalVerifications -= 1
      }
    },

    addFlaggedCitizen: (state, action: PayloadAction<FlaggedCitizen>) => {
      state.flaggedCitizens.unshift(action.payload)

      // Update statistics
      if (state.statistics) {
        state.statistics.flaggedCitizens += 1
      }
    },

    updateFlaggedCitizen: (state, action: PayloadAction<FlaggedCitizen>) => {
      const index = state.flaggedCitizens.findIndex((c) => c.id === action.payload.id)
      if (index >= 0) {
        state.flaggedCitizens[index] = action.payload
      }

      // Update selected if it's the same
      if (state.selectedCitizen?.id === action.payload.id) {
        state.selectedCitizen = action.payload
      }
    },

    removeFlaggedCitizen: (state, action: PayloadAction<string>) => {
      const id = action.payload
      state.flaggedCitizens = state.flaggedCitizens.filter((c) => c.id !== id)
      state.selectedFlaggedCitizens = state.selectedFlaggedCitizens.filter((cid) => cid !== id)

      // Update statistics
      if (state.statistics) {
        state.statistics.flaggedCitizens -= 1
      }
    },

    // === Quick Actions ===
    setQuickVerificationMode: (state, action: PayloadAction<boolean>) => {
      state.quickVerificationMode = action.payload
      if (action.payload) {
        state.showVerificationDialog = true
      }
    },

    addRecentSearch: (state, action: PayloadAction<string>) => {
      const search = action.payload.trim()
      if (search && !state.recentSearches.includes(search)) {
        state.recentSearches.unshift(search)
        // Keep only last 10 searches
        state.recentSearches = state.recentSearches.slice(0, 10)
      }
    },

    clearRecentSearches: (state) => {
      state.recentSearches = []
    },

    // === Real-time Features ===
    setRealTimeEnabled: (state, action: PayloadAction<boolean>) => {
      state.realTimeEnabled = action.payload
    },

    incrementPendingNotifications: (state) => {
      state.pendingNotifications += 1
    },

    clearPendingNotifications: (state) => {
      state.pendingNotifications = 0
    },

    // === Cache Management ===
    updateCacheTimestamp: (state) => {
      state.cacheTimestamp = Date.now()
    },

    // === Reset State ===
    resetState: () => initialState,
  },
})

export const {
  // Data Management
  setVerifications,
  setFlaggedCitizens,
  setStatistics,
  setVerificationPagination,
  setFlaggedPagination,

  // Loading & Error States
  setLoading,
  setError,
  clearError,

  // Filters Management
  setVerificationFilters,
  resetVerificationFilters,
  setFlaggedFilters,
  resetFlaggedFilters,

  // Selection Management
  setSelectedVerifications,
  toggleVerificationSelection,
  selectAllVerifications,
  clearVerificationSelection,
  setSelectedFlaggedCitizens,
  toggleFlaggedSelection,
  clearFlaggedSelection,

  // Modal/Dialog Management
  openVerificationDialog,
  closeVerificationDialog,
  openFlagDialog,
  closeFlagDialog,
  openDetailsDrawer,
  closeDetailsDrawer,
  openFiltersDrawer,
  closeFiltersDrawer,
  openExportDialog,
  closeExportDialog,

  // Data Operations
  addVerification,
  updateVerification,
  removeVerification,
  addFlaggedCitizen,
  updateFlaggedCitizen,
  removeFlaggedCitizen,

  // Quick Actions
  setQuickVerificationMode,
  addRecentSearch,
  clearRecentSearches,

  // Real-time Features
  setRealTimeEnabled,
  incrementPendingNotifications,
  clearPendingNotifications,

  // Cache Management
  updateCacheTimestamp,

  // Reset State
  resetState,
} = policeVerificationSlice.actions

// Selectors
export const selectVerifications = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.verifications

export const selectFlaggedCitizens = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.flaggedCitizens

export const selectVerificationStatistics = (state: {
  policeVerification: PoliceVerificationState
}) => state.policeVerification.statistics

export const selectVerificationFilters = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.verificationFilters

export const selectFlaggedFilters = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.flaggedFilters

export const selectVerificationLoading = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.loading

export const selectVerificationError = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.error

export const selectSelectedVerifications = (state: {
  policeVerification: PoliceVerificationState
}) => state.policeVerification.selectedVerifications

export const selectSelectedFlaggedCitizens = (state: {
  policeVerification: PoliceVerificationState
}) => state.policeVerification.selectedFlaggedCitizens

export const selectQuickVerificationMode = (state: {
  policeVerification: PoliceVerificationState
}) => state.policeVerification.quickVerificationMode

export const selectLastVerificationResult = (state: {
  policeVerification: PoliceVerificationState
}) => state.policeVerification.lastVerificationResult

export const selectRecentSearches = (state: { policeVerification: PoliceVerificationState }) =>
  state.policeVerification.recentSearches

export const selectPendingNotifications = (state: {
  policeVerification: PoliceVerificationState
}) => state.policeVerification.pendingNotifications

export default policeVerificationSlice.reducer
